import React, { useEffect, useState } from "react";
import { cls } from "cls";
import { RecaptchaDisclaimer, useReCaptcha } from "@wss/re-captcha";
import { toast } from "@clarkinc/zest-design-system/ui";
import {
  FormModel,
  FormFieldModel,
  FormFieldInputType,
  AlignmentType,
} from "../Models/PageCreatorPage";
import ContainerWrapper from "./ContainerWrapper";
import FormButton from "./FormButton";
import FormFieldInput from "./FormFields/FormFieldInput";
import fetchIsEmailValidForForm from "./fetchIsEmailValidForForm";
import fetchSaveFormSubmission from "./fetchSaveFormSubmission";
import SuccessAlert from "~/common/Alert/SuccessAlert";
import LeadGenImageVideo from "./LeadGenImageVideo";
import { getSanitizedHtml } from "../Utilities/htmlInputUtility";

interface FormField {
  value: string | number | boolean;
  error: string;
  label: string;
  fieldType: FormFieldInputType;
  isRequired: boolean;
  formFieldId: number;
  sortOrder: number;
}

interface FormData {
  [key: string]: FormField;
}

interface Props extends FormModel {
  description: string;
  pageId: number;
  sectionId: number;
}

const LeadGenForm = ({
  backgroundColor,
  backgroundImageUrl,
  useImageForBackground,
  hasDropShadow,
  hasOutline,
  containerColor,
  containerOutlineColor,
  headerTextColor,
  bodyTextColor,
  header,
  bodyText,
  buttonText,
  buttonTextColor,
  buttonBackgroundColor,
  formTextColor,
  formFields,
  description,
  pageId,
  sectionId,
  hasBottomPadding,
  hasTopPadding,
  formColumnCount,
  caption,
  captionTextColor,
  imageUrl,
  imageAltText,
  video,
  imageAlignment,
}: Props): JSX.Element => {
  const [formData, setFormData] = useState<FormData>({});
  const [hasError, setHasError] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const reCaptcha = useReCaptcha();
  const hasImageOrVideo = !!imageUrl || !!video;

  useEffect(() => {
    const initialFormData: FormData = {};

    formFields.forEach((field) => {
      initialFormData[field.formFieldId] = {
        value: "",
        error: "",
        label: field.label,
        fieldType: field.fieldType,
        isRequired: field.isRequired,
        formFieldId: field.formFieldId,
        sortOrder: field.sortOrder,
      };
    });

    setFormData(initialFormData);
  }, [formFields]);

  const handleOnSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const captchaToken = await reCaptcha.execute();
    if (captchaToken.isError) {
      toast.error("Please refresh your browser and try again", {
        title: "Failed to get reCaptcha status.",
        autoDismiss: false,
      });
      return;
    }

    const updatedFormData: FormData = { ...formData };
    let isError = false;

    setHasError(false);

    Object.keys(updatedFormData).forEach((key) => {
      const field = updatedFormData[key] as FormField;
      if (field.isRequired && !field.value) {
        field.error = `${field.label} is required`;

        isError = true;
      } else if (
        field.fieldType === FormFieldInputType.EmailAddress &&
        field.value
      ) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(field.value.toString())) {
          field.error = "Please enter valid email address";

          isError = true;
        }
      } else {
        field.error = "";
      }
    });

    setHasError(isError);

    setFormData(updatedFormData);

    if (isError) {
      return;
    }

    setIsSubmitting(true);

    const emailKey = Object.keys(updatedFormData).find((key) => {
      const field = updatedFormData[key];
      return field && field.fieldType === FormFieldInputType.EmailAddress;
    });

    const emailField = emailKey ? updatedFormData[emailKey] : undefined;
    if (emailField !== undefined) {
      const response = await fetchIsEmailValidForForm(
        String(emailField.value),
        sectionId,
      );

      if (response.isError) {
        emailField.error =
          "We encountered an error validating your email.  Please try again.";

        setFormData(updatedFormData);
        setIsSubmitting(false);
        setHasError(true);

        return;
      }

      const isEmailValid = response.value;

      if (!isEmailValid) {
        emailField.error =
          "The email address you entered has already been used. Please resubmit the form using a different email address.";

        setFormData(updatedFormData);
        setIsSubmitting(false);
        setHasError(true);

        return;
      }
    }

    const getNormalizedFieldName = (field: FormField) => {
      // Backend logic is dependent on email field so give consistent name
      // instead of what was input for label which can be anything
      if (field.fieldType === FormFieldInputType.EmailAddress) {
        return "Email Address";
      }

      return field.label;
    };

    const getNormalizedFieldValue = (field: FormField) => {
      if (
        field.fieldType === FormFieldInputType.TermsAndCondition &&
        !field.value
      ) {
        return "No";
      }

      return field.value;
    };

    const result = await fetchSaveFormSubmission({
      description,
      pageId,
      sectionId,
      formEntries: Object.values(updatedFormData).map((value) => ({
        formFieldId: value.formFieldId,
        formFieldName: getNormalizedFieldName(value),
        formFieldValue: getNormalizedFieldValue(value),
        sortOrder: value.sortOrder,
      })),
      recaptchaToken: captchaToken.value,
    });

    if (result.isError) {
      setIsError(true);

      toast(
        "Failed to submit your request, please check your connection and try again.",
        { variant: "error" },
      );
    }

    setIsSubmitted(true);
  };

  const setFormValue = (key: string, value: string | number | boolean) => {
    let strValue = value.toString();

    if (typeof value === "boolean") {
      strValue = value ? "Yes" : "No";
    }

    setFormData((prevFormData) => ({
      ...prevFormData,
      [key]: {
        ...prevFormData[key]!,
        value: strValue,
        error: "",
      },
    }));
  };

  const sortedFormFields = formFields?.sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );

  const handleTwoColumnLayout = (fieldType: number) => {
    const canBeTwoColumns =
      fieldType === FormFieldInputType.Address ||
      fieldType === FormFieldInputType.Custom ||
      fieldType === FormFieldInputType.Dropdown ||
      fieldType === FormFieldInputType.EmailAddress ||
      fieldType === FormFieldInputType.PhoneNumber;

    return canBeTwoColumns && formColumnCount === 2
      ? "col-span-1"
      : "col-span-2";
  };

  return (
    <div
      id="form-wrapper"
      className={cls(
        "px-6",
        "md:px-12",
        hasBottomPadding ? ["pb-12", "md:pb-24"] : "pb-0",
        hasTopPadding ? ["pt-12", "md:pt-24"] : "pt-0",
        "relative",
      )}
      style={{
        background: useImageForBackground
          ? `url("${backgroundImageUrl}") no-repeat center center`
          : backgroundColor,
        backgroundSize: "cover",
      }}
    >
      <LeadGenImageVideo
        imageAlignment={imageAlignment}
        hasImageOrVideo={hasImageOrVideo}
        imageUrl={imageUrl}
        imageAltText={imageAltText}
        video={video}
        hasCaption={!!caption}
        captionTextColor={captionTextColor}
        caption={caption}
        hasDropShadow={hasDropShadow}
        layoutStyles={cls(
          "flex",
          imageAlignment === AlignmentType.Left
            ? "flex-col"
            : "flex-col-reverse",
          "gap-6",
          "max-w-6xl",
          "mx-auto",
          "items-center",
          "lg:flex-row",
          "lg:gap-12",
        )}
        content={
          <ContainerWrapper
            hasContainer
            hasDropShadow={hasDropShadow}
            hasOutline={hasOutline}
            containerColor={containerColor}
            containerOutlineColor={containerOutlineColor}
            additionalClasses={cls(
              "h-fit",
              "w-full",
              hasImageOrVideo ? "lg:w-1/2" : "",
            )}
            hasRoundedBorder={hasBottomPadding && hasTopPadding}
          >
            <div
              className={cls(
                "flex",
                "flex-col",
                "px-6",
                "space-y-6",
                hasImageOrVideo ? "py-6" : "py-8 md:px-16 md:py-12",
              )}
            >
              <div
                className={`flex flex-col ${
                  hasImageOrVideo ? "max-w-3xl" : "max-w-2xl mx-auto"
                }`}
              >
                {!!header && (
                  <h2
                    className={`${cls(
                      "flex-wrap",
                      "font-bold",
                      "text-3xl",
                      "mb-1",
                      "md:text-4xl",
                      hasImageOrVideo ? "text-left" : "text-center",
                    )} [text-wrap:balance]`}
                    style={{ color: headerTextColor, textShadow: "none" }}
                  >
                    {header}
                  </h2>
                )}
                {!!bodyText && (
                  <p
                    className={`${cls(
                      "m-0",
                      "text-base",
                      "leading-tight",
                      hasImageOrVideo ? "text-left" : "text-center",
                    )} [text-wrap:balance]`}
                    style={{ color: bodyTextColor }}
                  >
                    {getSanitizedHtml(bodyText)}
                  </p>
                )}
              </div>
              <form onSubmit={handleOnSubmit}>
                <div
                  id="form-column-wrapper"
                  className={`${cls(
                    "grid",
                    "gap-x-8",
                    "auto-cols-auto",
                  )} md:grid-cols-${formColumnCount}`}
                >
                  {sortedFormFields.map((field: FormFieldModel) => (
                    <div
                      className={cls(
                        `md:${handleTwoColumnLayout(field.fieldType)}`,
                      )}
                      key={field.formFieldId}
                    >
                      <FormFieldInput
                        field={field}
                        formTextColor={formTextColor}
                        key={field.sortOrder}
                        setFormValue={setFormValue}
                        errorMessage={formData[field.formFieldId]?.error}
                      />
                    </div>
                  ))}
                </div>
                <FormButton
                  backgroundColor={buttonBackgroundColor}
                  textColor={buttonTextColor}
                  text={buttonText}
                  disabled={isSubmitting || isSubmitted}
                />
                {hasError && (
                  <span
                    className={cls(
                      "leading-3",
                      "h-5",
                      "mt-1",
                      "mb-0",
                      "text-xs",
                      "text-red-600",
                    )}
                  >
                    There were errors in your submission, please review your
                    entries and try again.
                  </span>
                )}
                <RecaptchaDisclaimer />
                {isSubmitted && !isError && (
                  <div className={cls("py-2")}>
                    <SuccessAlert className="mb-5">
                      Thanks, we have received your info!
                    </SuccessAlert>
                  </div>
                )}
              </form>
            </div>
          </ContainerWrapper>
        }
      />
    </div>
  );
};

export default LeadGenForm;
